// ---------------------------------------------------------------------------
// Selection Slice — tracks selected element IDs with tier awareness
// ---------------------------------------------------------------------------

import type { SelectionTier, CogoAppDef, CogoBlockDef } from './types'
import { collectElementIds, findElementInTree } from './canvas-slice'
// Dynamic, not static: `studio-store.ts` statically imports `project-slice.ts`,
// which statically imports THIS module's sibling `editor-store.ts` (the
// page-editor bridge), closing a cycle back here. In the app, `studio-store.ts`
// is always evaluated first, so a static import here would be safe today — but
// a test (or any future module) that imports `editor-store.ts`/this module
// BEFORE `studio-store.ts` reverses the cycle: `studio-store.ts` gets pulled in
// and evaluated while `project-slice.ts` is still mid-initialization, and its
// `createProjectSlice` export is not yet defined when `studio-store.ts`'s own
// top-level `create()` call tries to spread it in — `TypeError:
// createProjectSlice is not a function`. Every use below already runs inside a
// `queueMicrotask` callback (deferred past synchronous module evaluation), so
// resolving the module lazily there costs nothing and breaks the cycle at this,
// its narrowest edge. See `src/store/__tests__/project-slice-circular-import.test.ts`
// and `src/store/recent-projects-key.ts`'s header for the wider landmine.
async function getStudioStore() {
  const { useStudioStore } = await import('@/store/studio-store')
  return useStudioStore
}

export interface SelectionSlice {
  selectedIds: string[]
  selectionTier: SelectionTier
  selectedBlockName: string | null
  selectedPageId: string | null

  select: (id: string, blockName?: string) => void
  selectMultiple: (ids: string[], blockName?: string) => void
  addToSelection: (id: string) => void
  removeFromSelection: (id: string) => void
  toggleSelection: (id: string) => void
  clearSelection: () => void
  selectPage: (pageId: string) => void
  selectBlock: (blockName: string) => void
  selectAll: () => void
}
type Set = (fn: (state: SelectionSlice & {
  app: CogoAppDef | null
  blocks: Record<string, CogoBlockDef>
  activePageId: string | null
}) => void) => void
type Get = () => SelectionSlice & {
  app: CogoAppDef | null
  blocks: Record<string, CogoBlockDef>
  activePageId: string | null
  getActivePageBlocks: () => CogoBlockDef[]
}

export function createSelectionSlice(set: Set, get: Get, _api: unknown): SelectionSlice {
  return {
    selectedIds: [],
    selectionTier: 'component',
    selectedBlockName: null,
    selectedPageId: null,

    select: (id, blockName) =>
      set((state) => {
        state.selectedIds = [id]
        state.selectionTier = 'component'
        if (blockName) state.selectedBlockName = blockName
        // Sync selection to chat context (EDIT-04)
        if (blockName) {
          const block = state.blocks[blockName]
          const element = block ? findElementInTree(block.ui.root, id) : undefined
          const elementType = element?.type
          const blockType = block?.meta?.type
          queueMicrotask(() => {
            void getStudioStore().then((useStudioStore) => {
              const studio = useStudioStore.getState()
              studio.setChatContext({
                ...studio.chatContext,
                selectionSurface: 'page',
                selectedElementId: id,
                selectedBlockName: blockName,
                selectedBlockType: blockType,
                selectedElementType: elementType,
                selectedPropertyKey: undefined,
              })
            })
          })
        }
      }),

    selectMultiple: (ids, blockName) =>
      set((state) => {
        state.selectedIds = ids
        state.selectionTier = 'component'
        if (blockName) state.selectedBlockName = blockName
      }),

    addToSelection: (id) =>
      set((state) => {
        if (!state.selectedIds.includes(id)) {
          state.selectedIds.push(id)
        }
      }),

    removeFromSelection: (id) =>
      set((state) => {
        state.selectedIds = state.selectedIds.filter((s) => s !== id)
      }),

    toggleSelection: (id) =>
      set((state) => {
        const idx = state.selectedIds.indexOf(id)
        if (idx === -1) {
          state.selectedIds.push(id)
        } else {
          state.selectedIds.splice(idx, 1)
        }
      }),

    clearSelection: () =>
      set((state) => {
        state.selectedIds = []
        state.selectedBlockName = null
        state.selectedPageId = null
        // Clear selection from chat context (EDIT-04)
        queueMicrotask(() => {
          void getStudioStore().then((useStudioStore) => {
            const ctx = useStudioStore.getState().chatContext
            useStudioStore.getState().setChatContext({
              ...ctx,
              selectionSurface: undefined,
              selectedElementId: undefined,
              selectedBlockName: undefined,
              selectedBlockType: undefined,
              selectedElementType: undefined,
              selectedPropertyKey: undefined,
            })
          })
        })
      }),

    selectPage: (pageId) =>
      set((state) => {
        const page = state.app?.pages.find((candidate) => candidate.id === pageId)
        const pageName = page?.title ?? pageId
        state.selectionTier = 'page'
        state.selectedIds = []
        state.selectedBlockName = null
        state.selectedPageId = pageId
        // Sync page selection to chat context (CHAT-05)
        queueMicrotask(() => {
          void getStudioStore().then((useStudioStore) => {
            const store = useStudioStore.getState()
            store.setChatContext({
              ...store.chatContext,
              pageId,
              pageName,
              selectionSurface: 'page',
              selectedElementId: undefined,
              selectedBlockName: undefined,
              selectedBlockType: undefined,
              selectedElementType: undefined,
              selectedPropertyKey: undefined,
            })
          })
        })
      }),

    selectBlock: (blockName) =>
      set((state) => {
        state.selectedBlockName = blockName
        state.selectionTier = 'block'
        // Select all element IDs in this block
        const block = state.blocks[blockName]
        if (block) {
          state.selectedIds = collectElementIds(block.ui.root)
        }
        // Sync block selection to chat context (EDIT-04)
        const blockType = block?.meta?.type
        queueMicrotask(() => {
          void getStudioStore().then((useStudioStore) => {
            useStudioStore.getState().setChatContext({
              ...useStudioStore.getState().chatContext,
              selectionSurface: 'page',
              selectedElementId: undefined,
              selectedBlockName: blockName,
              selectedBlockType: blockType,
              selectedElementType: undefined,
              selectedPropertyKey: undefined,
            })
          })
        })
      }),

    selectAll: () => {
      const blocks = get().getActivePageBlocks()
      const allIds: string[] = []
      for (const block of blocks) {
        allIds.push(...collectElementIds(block.ui.root))
      }
      // Set selectedBlockName so copy/cut/delete work after selectAll.
      // For single-block pages this is straightforward; for multi-block
      // pages we pick the first block (multi-block operations iterate
      // through selectedIds which may span blocks).
      const firstBlockName = blocks.length > 0
        ? (blocks[0].meta?.name ?? null)
        : null
      set((state) => {
        state.selectedIds = allIds
        state.selectedBlockName = firstBlockName
      })
    },
  }
}
